import React, { useState } from 'react';
import { Grid, Ruler, AlignLeft, Maximize, Download } from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const LayoutGridSystem: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'spacing' | 'grid' | 'alignment' | 'breakpoints'>('spacing');

  const SpacingExample: React.FC<{
    title: string;
    description: string;
    spacing: number;
    unit: 'px' | 'rem' | 'em';
    usage: string;
  }> = ({ title, description, spacing, unit, usage }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
          <p className="text-sm text-[var(--text-sec)]">{description}</p>
        </div>
        <div className="text-right">
          <div className="text-lg font-heading font-semibold text-[var(--text-pri)]">{spacing}{unit}</div>
          <div className="text-xs text-[var(--text-ter)]">{usage}</div>
        </div>
      </div>

      <div className="bg-[var(--surface-raised)] rounded p-3">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-[var(--mustard)] rounded"></div>
          <div
            className="bg-[var(--slate)] rounded"
            style={{
              width: `${spacing}px`,
              height: '16px'
            }}
          ></div>
          <div className="w-4 h-4 bg-sage-600 rounded"></div>
        </div>
        <div className="text-xs text-[var(--text-ter)] mt-2">
          Visual representation of {spacing}{unit} spacing
        </div>
      </div>
    </div>
  );

  const GridExample: React.FC<{
    title: string;
    description: string;
    columns: number;
    gap: number;
    children: React.ReactNode;
  }> = ({ title, description, columns, gap, children }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
      <div className="mb-3">
        <h3 className="font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
        <div className="text-xs text-[var(--text-ter)] mt-1">
          {columns} columns • {gap}px gap
        </div>
      </div>

      <div
        className="rounded border border-dashed border-[var(--rule)] p-4"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: `${gap}px`
        }}
      >
        {children}
      </div>
    </div>
  );

  const AlignmentExample: React.FC<{
    title: string;
    description: string;
    alignment: 'left' | 'center' | 'right' | 'justify';
    children: React.ReactNode;
  }> = ({ title, description, alignment, children }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
      <div className="mb-3">
        <h3 className="font-heading font-semibold text-[var(--text-pri)]">{title}</h3>
        <p className="text-sm text-[var(--text-sec)]">{description}</p>
      </div>

      <div
        className="bg-[var(--surface-raised)] rounded p-4 border border-dashed border-[var(--rule)]"
        style={{ textAlign: alignment }}
      >
        {children}
      </div>
    </div>
  );

  const BreakpointExample: React.FC<{
    name: string;
    size: string;
    description: string;
    usage: string;
  }> = ({ name, size, description, usage }) => (
    <div className="border border-[var(--rule)] bg-[var(--surface)] p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading font-semibold text-[var(--text-pri)]">{name}</h3>
        <div className="text-sm font-mono bg-[var(--surface-raised)] px-2 py-1 rounded">{size}</div>
      </div>
      <p className="text-sm text-[var(--text-sec)] mb-2">{description}</p>
      <p className="text-xs text-[var(--text-ter)]">{usage}</p>
    </div>
  );

  return (
    <div>
      <ProductDesignPageHeader
        title="Layout & Grid System"
        eyebrow="Structure · breakpoints"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="Comprehensive layout system with spacing, alignment, margins, gutters, white space rules for documents, presentations, web, and diagrams."
      />

      {/* System Overview */}
      <ProductDesignSection title="Layout System Overview">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          Our layout system ensures visual harmony and consistency across all Peak Priorities assets.
          Every spacing, alignment, and grid decision reinforces the brand's clarity and precision.
        </p>

        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Spacing Scale</h3>
            <p className="text-sm text-[var(--text-sec)]">8px base unit with consistent scaling for all spacing needs</p>
          </div>
          <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Grid System</h3>
            <p className="text-sm text-[var(--text-sec)]">12-column grid with flexible breakpoints for all screen sizes</p>
          </div>
          <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Alignment Rules</h3>
            <p className="text-sm text-[var(--text-sec)]">Consistent alignment patterns for text, images, and components</p>
          </div>
          <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
            <h3 className="font-heading font-semibold text-[var(--text-pri)] mb-2">White Space</h3>
            <p className="text-sm text-[var(--text-sec)]">Strategic use of white space for visual breathing room</p>
          </div>
        </div>
      </ProductDesignSection>

      {/* Tab Navigation */}
      <div className="flex flex-wrap justify-center gap-4 mb-8">
        <button
          onClick={() => setActiveTab('spacing')}
          className={`flex items-center gap-2 px-6 py-3  font-semibold transition-colors ${
            activeTab === 'spacing'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
          }`}
        >
          <Ruler className="h-5 w-5" />
          Spacing System
        </button>
        <button
          onClick={() => setActiveTab('grid')}
          className={`flex items-center gap-2 px-6 py-3  font-semibold transition-colors ${
            activeTab === 'grid'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
          }`}
        >
          <Grid className="h-5 w-5" />
          Grid System
        </button>
        <button
          onClick={() => setActiveTab('alignment')}
          className={`flex items-center gap-2 px-6 py-3  font-semibold transition-colors ${
            activeTab === 'alignment'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
          }`}
        >
          <AlignLeft className="h-5 w-5" />
          Alignment
        </button>
        <button
          onClick={() => setActiveTab('breakpoints')}
          className={`flex items-center gap-2 px-6 py-3  font-semibold transition-colors ${
            activeTab === 'breakpoints'
              ? 'bg-[var(--slate)] text-white'
              : 'bg-[var(--surface-raised)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]'
          }`}
        >
          <Maximize className="h-5 w-5" />
          Breakpoints
        </button>
      </div>

      {/* Spacing System */}
      {activeTab === 'spacing' && (
        <div>
          <ProductDesignSection title="Spacing Scale">
            <p className="text-[var(--text-sec)] mb-6">
              Our spacing system is based on an 8px base unit, ensuring consistent visual rhythm across all applications.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <SpacingExample
                title="4px (0.25rem)"
                description="Micro spacing for tight layouts"
                spacing={4}
                unit="px"
                usage="Icon padding, fine details"
              />
              <SpacingExample
                title="8px (0.5rem)"
                description="Base unit for all spacing"
                spacing={8}
                unit="px"
                usage="Component padding, small gaps"
              />
              <SpacingExample
                title="12px (0.75rem)"
                description="Small spacing for related elements"
                spacing={12}
                unit="px"
                usage="Form field spacing, small margins"
              />
              <SpacingExample
                title="16px (1rem)"
                description="Standard spacing for most elements"
                spacing={16}
                unit="px"
                usage="Button padding, standard margins"
              />
              <SpacingExample
                title="24px (1.5rem)"
                description="Medium spacing for sections"
                spacing={24}
                unit="px"
                usage="Card padding, section spacing"
              />
              <SpacingExample
                title="32px (2rem)"
                description="Large spacing for major sections"
                spacing={32}
                unit="px"
                usage="Page margins, large gaps"
              />
              <SpacingExample
                title="48px (3rem)"
                description="Extra large spacing for emphasis"
                spacing={48}
                unit="px"
                usage="Hero sections, major divisions"
              />
              <SpacingExample
                title="64px (4rem)"
                description="Maximum spacing for dramatic effect"
                spacing={64}
                unit="px"
                usage="Page sections, dramatic spacing"
              />
              <SpacingExample
                title="96px (6rem)"
                description="Hero spacing for maximum impact"
                spacing={96}
                unit="px"
                usage="Landing pages, hero sections"
              />
            </div>
          </ProductDesignSection>

          {/* Spacing Applications */}
          <ProductDesignSection title="Spacing Applications">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Web Applications</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Container Padding</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-4">
                      <div className="bg-[var(--surface-raised)] p-4 rounded">
                        <p className="text-sm">Container with 16px padding</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Component Spacing</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-4">
                      <div className="space-y-3">
                        <div className="bg-[var(--mustard-dim)] p-3 rounded">Component 1</div>
                        <div className="bg-sage-100 p-3 rounded">Component 2</div>
                        <div className="bg-coral-100 p-3 rounded">Component 3</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Print Applications</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Document Margins</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-4">
                      <div className="bg-[var(--surface-raised)] p-6 rounded">
                        <p className="text-sm">Document with 24px margins</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Section Spacing</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-4">
                      <div className="space-y-6">
                        <div className="bg-[var(--mustard-dim)] p-4 rounded">Section 1</div>
                        <div className="bg-sage-100 p-4 rounded">Section 2</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Grid System */}
      {activeTab === 'grid' && (
        <div>
          <ProductDesignSection title="Grid System">
            <p className="text-[var(--text-sec)] mb-6">
              Our 12-column grid system provides flexible layouts for all screen sizes and content types.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <GridExample
                title="12-Column Grid"
                description="Full-width layout with 12 columns"
                columns={12}
                gap={16}
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <div key={i} className="bg-[var(--mustard-dim)] h-8 rounded flex items-center justify-center text-xs font-medium">
                    {i + 1}
                  </div>
                ))}
              </GridExample>

              <GridExample
                title="6-Column Grid"
                description="Two-column layout"
                columns={6}
                gap={16}
              >
                {Array.from({ length: 6 }, (_, i) => (
                  <div key={i} className="bg-[var(--surface-raised)] h-8 rounded flex items-center justify-center text-xs font-medium">
                    {i + 1}
                  </div>
                ))}
              </GridExample>

              <GridExample
                title="4-Column Grid"
                description="Three-column layout"
                columns={4}
                gap={16}
              >
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="bg-sage-100 h-8 rounded flex items-center justify-center text-xs font-medium">
                    {i + 1}
                  </div>
                ))}
              </GridExample>

              <GridExample
                title="3-Column Grid"
                description="Four-column layout"
                columns={3}
                gap={16}
              >
                {Array.from({ length: 3 }, (_, i) => (
                  <div key={i} className="bg-coral-100 h-8 rounded flex items-center justify-center text-xs font-medium">
                    {i + 1}
                  </div>
                ))}
              </GridExample>
            </div>
          </ProductDesignSection>

          {/* Grid Applications */}
          <ProductDesignSection title="Grid Applications">
            <div className="space-y-8">
              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Card Layouts</h3>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                >
                  {Array.from({ length: 6 }, (_, i) => (
                    <div key={i} className="bg-[var(--surface)] border border-[var(--rule)] p-4">
                      <div className="w-full h-20 bg-[var(--mustard-dim)] rounded mb-3"></div>
                      <h4 className="font-heading font-semibold text-[var(--text-pri)] mb-2">Card {i + 1}</h4>
                      <p className="text-sm text-[var(--text-sec)]">Responsive card layout using grid system</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Content Layouts</h3>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 bg-[var(--surface)] border border-[var(--rule)] p-6">
                    <h4 className="font-heading font-semibold text-[var(--text-pri)] mb-3">Main Content</h4>
                    <p className="text-[var(--text-sec)]">Two-thirds width for primary content areas</p>
                  </div>
                  <div className="bg-[var(--surface)] border border-[var(--rule)] p-6">
                    <h4 className="font-heading font-semibold text-[var(--text-pri)] mb-3">Sidebar</h4>
                    <p className="text-[var(--text-sec)]">One-third width for secondary content</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Alignment */}
      {activeTab === 'alignment' && (
        <div>
          <ProductDesignSection title="Alignment System">
            <p className="text-[var(--text-sec)] mb-6">
              Consistent alignment patterns ensure visual harmony and improve readability across all applications.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <AlignmentExample
                title="Left Alignment"
                description="Default for most text content"
                alignment="left"
              >
                <p className="text-[var(--text-sec)]">This is left-aligned text, which is the most common alignment for body text and provides the best readability for most content.</p>
              </AlignmentExample>

              <AlignmentExample
                title="Center Alignment"
                description="For headings and special emphasis"
                alignment="center"
              >
                <p className="text-[var(--text-sec)]">This is center-aligned text, typically used for headings, titles, and special emphasis elements.</p>
              </AlignmentExample>

              <AlignmentExample
                title="Right Alignment"
                description="For numbers and specific layouts"
                alignment="right"
              >
                <p className="text-[var(--text-sec)]">This is right-aligned text, commonly used for numbers, dates, and specific layout requirements.</p>
              </AlignmentExample>

              <AlignmentExample
                title="Justified Alignment"
                description="For formal documents and print"
                alignment="justify"
              >
                <p className="text-[var(--text-sec)]">This is justified text, which creates clean edges on both sides and is commonly used in formal documents and print materials.</p>
              </AlignmentExample>
            </div>
          </ProductDesignSection>

          {/* Alignment Guidelines */}
          <ProductDesignSection title="Alignment Guidelines">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Text Alignment</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Body Text</h4>
                    <p className="text-sm text-[var(--text-sec)]">Always left-aligned for optimal readability</p>
                  </div>

                  <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Headings</h4>
                    <p className="text-sm text-[var(--text-sec)]">Left-aligned for hierarchy, center for emphasis</p>
                  </div>

                  <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Numbers</h4>
                    <p className="text-sm text-[var(--text-sec)]">Right-aligned for easy comparison</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Element Alignment</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Images</h4>
                    <p className="text-sm text-[var(--text-sec)]">Center-aligned for visual balance</p>
                  </div>

                  <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Buttons</h4>
                    <p className="text-sm text-[var(--text-sec)]">Left-aligned in forms, center for CTAs</p>
                  </div>

                  <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Tables</h4>
                    <p className="text-sm text-[var(--text-sec)]">Consistent alignment within columns</p>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* Breakpoints */}
      {activeTab === 'breakpoints' && (
        <div>
          <ProductDesignSection title="Responsive Breakpoints">
            <p className="text-[var(--text-sec)] mb-6">
              Our breakpoint system ensures optimal layouts across all devices and screen sizes.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <BreakpointExample
                name="Mobile"
                size="320px - 767px"
                description="Small screens and mobile devices"
                usage="Single column layouts, stacked content"
              />
              <BreakpointExample
                name="Tablet"
                size="768px - 1023px"
                description="Medium screens and tablets"
                usage="Two-column layouts, adapted spacing"
              />
              <BreakpointExample
                name="Desktop"
                size="1024px - 1439px"
                description="Large screens and desktops"
                usage="Multi-column layouts, full spacing"
              />
              <BreakpointExample
                name="Large Desktop"
                size="1440px+"
                description="Extra large screens"
                usage="Maximum content width, enhanced spacing"
              />
            </div>
          </ProductDesignSection>

          {/* Breakpoint Applications */}
          <ProductDesignSection title="Breakpoint Applications">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Navigation</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Mobile Navigation</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-3">
                      <div className="flex justify-between items-center">
                        <div className="w-8 h-8 bg-[var(--mustard)] rounded"></div>
                        <div className="w-6 h-6 bg-[var(--text-ter)] rounded"></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Desktop Navigation</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-3">
                      <div className="flex justify-between items-center">
                        <div className="w-8 h-8 bg-[var(--mustard)] rounded"></div>
                        <div className="flex gap-4">
                          <div className="w-16 h-4 bg-[var(--text-ter)] rounded"></div>
                          <div className="w-16 h-4 bg-[var(--text-ter)] rounded"></div>
                          <div className="w-16 h-4 bg-[var(--text-ter)] rounded"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">Content Layouts</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Mobile: Single Column</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-3">
                      <div className="space-y-2">
                        <div className="w-full h-8 bg-[var(--mustard-dim)] rounded"></div>
                        <div className="w-full h-8 bg-sage-100 rounded"></div>
                        <div className="w-full h-8 bg-coral-100 rounded"></div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-[var(--surface-raised)] p-4">
                    <h4 className="font-medium text-[var(--text-pri)] mb-2">Desktop: Multi-Column</h4>
                    <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-3">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="h-8 bg-[var(--mustard-dim)] rounded"></div>
                        <div className="h-8 bg-sage-100 rounded"></div>
                        <div className="h-8 bg-coral-100 rounded"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ProductDesignSection>
        </div>
      )}

      {/* White Space Guidelines */}
      <ProductDesignSection title="White Space Guidelines">
        <p className="text-[var(--text-sec)] mb-6">
          Strategic use of white space creates visual breathing room and improves content comprehension.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">White Space Principles</h3>
            <div className="space-y-4">
              <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                <h4 className="font-medium text-[var(--text-pri)] mb-2">Grouping</h4>
                <p className="text-sm text-[var(--text-sec)]">Use white space to group related elements and separate different sections</p>
              </div>

              <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                <h4 className="font-medium text-[var(--text-pri)] mb-2">Hierarchy</h4>
                <p className="text-sm text-[var(--text-sec)]">More white space around important elements to create visual hierarchy</p>
              </div>

              <div className="bg-[var(--surface-raised)] border border-[var(--rule)] p-4">
                <h4 className="font-medium text-[var(--text-pri)] mb-2">Balance</h4>
                <p className="text-sm text-[var(--text-sec)]">Balance content and white space for visual harmony</p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-4">White Space Applications</h3>
            <div className="space-y-4">
              <div className="bg-[var(--surface-raised)] p-4">
                <h4 className="font-medium text-[var(--text-pri)] mb-2">Document Layout</h4>
                <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-4">
                  <div className="space-y-6">
                    <div className="bg-[var(--mustard-dim)] p-3 rounded">Header Section</div>
                    <div className="bg-sage-100 p-3 rounded">Content Section</div>
                    <div className="bg-coral-100 p-3 rounded">Footer Section</div>
                  </div>
                </div>
              </div>

              <div className="bg-[var(--surface-raised)] p-4">
                <h4 className="font-medium text-[var(--text-pri)] mb-2">Component Spacing</h4>
                <div className="bg-[var(--surface)] border border-dashed border-[var(--rule)] p-4">
                  <div className="space-y-4">
                    <div className="bg-[var(--mustard-dim)] p-2 rounded">Component 1</div>
                    <div className="bg-sage-100 p-2 rounded">Component 2</div>
                    <div className="bg-coral-100 p-2 rounded">Component 3</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ProductDesignSection>

      {/* Implementation Guidelines */}
      <ProductDesignSection title="Implementation Guidelines">
        <p className="text-[var(--text-sec)] max-w-3xl mb-6">
          Use these layout and grid principles consistently across all Peak Priorities assets.
          The system ensures visual harmony and improves user experience across all touchpoints.
        </p>
        <div className="flex gap-4 flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white font-semibold hover:bg-[var(--hover-tint)] transition-colors">
            <Download className="h-5 w-5" />
            Download Guidelines
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--surface)] text-[var(--text-pri)] border border-[var(--rule)] font-semibold hover:bg-[var(--hover-tint)] transition-colors">
            <Grid className="h-5 w-5" />
            View Examples
          </button>
        </div>
      </ProductDesignSection>
    </div>
  );
};
